// /Users/aminajackson/Git-projects/pro-maximizer/frontend/src/SettingsPage.tsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  GitHub as GitHubIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Dns as DnsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { useAppContext } from './AppContext';

import { API_BASE_URL } from './api';

// --- Component for a single setting section ---
interface SettingSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onSave: () => void;
  onRequestChange: () => void;
  isEditable: boolean;
  isLoading: boolean;
  apiFeedback: { type: 'success' | 'error'; message: string } | null;
}

const SettingSection: React.FC<SettingSectionProps> = ({
  title,
  icon,
  children,
  onSave,
  onRequestChange,
  isEditable,
  isLoading,
  apiFeedback,
}) => {
  // Clone children to pass down the disabled prop
  const childrenWithDisabledProp = React.Children.map(children, child => {
    if (React.isValidElement(child) && child.type !== Typography) {
      return React.cloneElement(child as React.ReactElement<any>, {
        disabled: !isEditable || isLoading,
      });
    }
    return child;
  });

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar>{icon}</Avatar>
          <Typography>{title}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {childrenWithDisabledProp}
          <Collapse in={!!apiFeedback}>
            {apiFeedback && (
              <Alert severity={apiFeedback.type} sx={{ mb: 2 }}>
                {apiFeedback.message}
              </Alert>
            )}
          </Collapse>
          <Box sx={{ textAlign: 'right' }}>
            {isEditable ? (
              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={onSave}
                disabled={isLoading}
              >
                {isLoading ? 'Testing...' : 'Test & Save Connection'}
              </Button>
            ) : (
              <Button variant="outlined" onClick={onRequestChange}>
                Request Change
              </Button>
            )}
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const SettingsPage = () => {
  const { companyName, teamName } = useAppContext();

  // --- MOCK CURRENT USER ROLE ---
  const [currentUserRole, setCurrentUserRole] = useState('Owner');
  const canEditAdminSettings = currentUserRole === 'Owner' || currentUserRole === 'Admin';
  const canEditConnections = ['Owner', 'Admin', 'Edit'].includes(currentUserRole);

  // --- State for API interactions ---
  const [loadingSection, setLoadingSection] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ [key: string]: { type: 'success' | 'error'; message: string } | null }>({});

  // State for each connection type
  const [adminEmail, setAdminEmail] = useState('admin@pro-maximizer.com');
  const [users, setUsers] = useState([
      { id: 1, name: 'Amina Jackson', email: 'amina.jackson@example.com', role: 'Owner' },
      { id: 2, name: 'Leo Vinci', email: 'leo.vinci@example.com', role: 'Admin' },
      { id: 3, name: 'Jane Doe', email: 'jane.doe@example.com', role: 'Edit' },
      { id: 4, name: 'John Smith', email: 'john.smith@example.com', role: 'Read Only' },
  ]);

  // State for connections, now including a name field
  const [githubConfig, setGithubConfig] = useState({ name: '', url: '', token: '' });
  const [dbType, setDbType] = useState('');
  const [dbConfig, setDbConfig] = useState({
    name: '', host: '', port: '', user: '', password: '', dbname: '',
    account: '', warehouse: '', schema: '', serviceAccount: '',
  });
  const [storageType, setStorageType] = useState('');
  const [storageConfig, setStorageConfig] = useState({ name: '', bucket: '', region: '', accessKey: '', secretKey: '', connectionString: '', serviceAccount: '' });
  const [gcpConfig, setGcpConfig] = useState({ name: '', credentials: '' });
  const [azureConfig, setAzureConfig] = useState({ name: '', credentials: '' });
  const [awsConfig, setAwsConfig] = useState({ name: '', accessKeyId: '', secretAccessKey: '' });

  const setSectionFeedback = (section: string, type: 'success' | 'error', message: string) => {
    setFeedback(prev => ({ ...prev, [section]: { type, message } }));
  };

  const handleSave = async (section: string, type: string, name: string, credentials: any) => {
    if (!name) {
      setSectionFeedback(section, 'error', 'Connection Name is required.');
      return;
    }

    setLoadingSection(section);
    setSectionFeedback(section, 'success', 'Saving connection...');

    const payload = { name, type, credentials };

    try {
      const saveResponse = await fetch(`${API_BASE_URL}/api/settings/connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json();
        throw new Error(errorData.detail || 'Failed to save connection.');
      }

      setSectionFeedback(section, 'success', 'Connection saved successfully!');

    } catch (error: any) {
      setSectionFeedback(section, 'error', error.message);
    } finally {
      setLoadingSection(null);
    }
  };

  const handleRequestChange = (section: string) => {
    alert(`Change request for ${section} has been submitted for admin approval.`);
  };

  const handleRoleChange = (userId: number, newRole: string) => {
    setUsers(currentUsers =>
        currentUsers.map(user =>
            user.id === userId ? { ...user, role: newRole } : user
        )
    );
  };

  const renderDbFields = () => {
    if (!dbType) return null;
    const commonFields = (
        <>
            <TextField label="Host" value={dbConfig.host} onChange={(e) => setDbConfig(p => ({...p, host: e.target.value}))} />
            <TextField label="Port" value={dbConfig.port} onChange={(e) => setDbConfig(p => ({...p, port: e.target.value}))} />
            <TextField label="User" value={dbConfig.user} onChange={(e) => setDbConfig(p => ({...p, user: e.target.value}))} />
            <TextField label="Password" type="password" value={dbConfig.password} onChange={(e) => setDbConfig(p => ({...p, password: e.target.value}))} />
            <TextField label="Database Name" value={dbConfig.dbname} onChange={(e) => setDbConfig(p => ({...p, dbname: e.target.value}))} />
        </>
    );
    switch (dbType) {
      case 'postgres':
      case 'mysql':
      case 'sqlserver':
      case 'redshift':
        return commonFields;
      case 'mongodb':
        return <TextField label="MongoDB Connection URI" placeholder="mongodb://user:pass@host:port/dbname" value={dbConfig.host} onChange={(e) => setDbConfig(p => ({...p, host: e.target.value}))} />;
      case 'redis':
        return <TextField label="Redis Connection URI" placeholder="redis://:password@host:port/0" value={dbConfig.host} onChange={(e) => setDbConfig(p => ({...p, host: e.target.value}))} />;
      case 'snowflake':
        return (
          <>
            <TextField label="Account Identifier" placeholder="e.g., xy12345.us-east-1" value={dbConfig.account} onChange={(e) => setDbConfig(p => ({...p, account: e.target.value}))} />
            <TextField label="Warehouse" value={dbConfig.warehouse} onChange={(e) => setDbConfig(p => ({...p, warehouse: e.target.value}))} />
            <TextField label="Database" value={dbConfig.dbname} onChange={(e) => setDbConfig(p => ({...p, dbname: e.target.value}))} />
            <TextField label="Schema" value={dbConfig.schema} onChange={(e) => setDbConfig(p => ({...p, schema: e.target.value}))} />
            <TextField label="User" value={dbConfig.user} onChange={(e) => setDbConfig(p => ({...p, user: e.target.value}))} />
            <TextField label="Password" type="password" value={dbConfig.password} onChange={(e) => setDbConfig(p => ({...p, password: e.target.value}))} />
          </>
        );
      case 'bigquery':
        return <TextField label="GCP Service Account JSON" multiline rows={4} value={dbConfig.serviceAccount} onChange={(e) => setDbConfig(p => ({...p, serviceAccount: e.target.value}))} />;
      default:
        return null;
    }
  };

  const renderStorageFields = () => {
    switch (storageType) {
      case 's3':
        return (
          <>
            <TextField label="Bucket Name" value={storageConfig.bucket} onChange={e => setStorageConfig(p => ({...p, bucket: e.target.value}))} />
            <TextField label="Region" value={storageConfig.region} onChange={e => setStorageConfig(p => ({...p, region: e.target.value}))} />
            <TextField label="Access Key ID" value={storageConfig.accessKey} onChange={e => setStorageConfig(p => ({...p, accessKey: e.target.value}))} />
            <TextField label="Secret Access Key" type="password" value={storageConfig.secretKey} onChange={e => setStorageConfig(p => ({...p, secretKey: e.target.value}))} />
          </>
        );
      case 'gcs':
        return (
          <>
            <TextField label="Bucket Name" value={storageConfig.bucket} onChange={e => setStorageConfig(p => ({...p, bucket: e.target.value}))} />
            <TextField label="GCP Service Account JSON" multiline rows={4} value={storageConfig.serviceAccount} onChange={e => setStorageConfig(p => ({...p, serviceAccount: e.target.value}))} />
          </>
        );
      case 'azure':
        return <TextField label="Azure Blob Storage Connection String" type="password" value={storageConfig.connectionString} onChange={e => setStorageConfig(p => ({...p, connectionString: e.target.value}))} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar><AdminPanelSettingsIcon /></Avatar>
                <Typography>General</Typography>
            </Stack>
        </AccordionSummary>
        <AccordionDetails>
            <Stack spacing={2}>
                <TextField label="Company Name" value={companyName} InputProps={{ readOnly: true }} helperText="Company name is managed by the organization owner." />
                <TextField label="Team Name or Org" value={teamName} InputProps={{ readOnly: true }} helperText="Team name is managed by the organization owner." />
                <TextField label="Admin Email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} helperText="This email will be used as the 'from' address for user invitations." disabled={!canEditAdminSettings} />
                 <Box sx={{ textAlign: 'right' }}>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={() => console.log('Saving general settings')} disabled={!canEditAdminSettings}>
                        Save General Settings
                    </Button>
                </Box>
            </Stack>
        </AccordionDetails>
      </Accordion>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        User Management
      </Typography>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar><ManageAccountsIcon /></Avatar>
                <Typography>User Directory</Typography>
            </Stack>
        </AccordionSummary>
        <AccordionDetails>
            <Stack spacing={2}>
                <List>
                    {users.map((user, index) => (
                        <React.Fragment key={user.id}>
                            <ListItem secondaryAction={
                                <FormControl size="small" sx={{ minWidth: 120 }} disabled={!canEditAdminSettings}>
                                    <InputLabel>Role</InputLabel>
                                    <Select value={user.role} label="Role" onChange={(e) => handleRoleChange(user.id, e.target.value)} disabled={user.role === 'Owner'}>
                                        <MenuItem value="Read Only">Read Only</MenuItem>
                                        <MenuItem value="Edit">Edit</MenuItem>
                                        <MenuItem value="Admin">Admin</MenuItem>
                                        <MenuItem value="Owner">Owner</MenuItem>
                                    </Select>
                                </FormControl>
                            }>
                                <ListItemText primary={user.name} secondary={user.email} />
                            </ListItem>
                            {index < users.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
                </List>
                <Box sx={{ textAlign: 'right' }}>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={() => console.log('Saving roles')} disabled={!canEditAdminSettings}>
                        Save Role Changes
                    </Button>
                </Box>
            </Stack>
        </AccordionDetails>
      </Accordion>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Connections & Integrations
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Configure connections to external services to enable more features across your projects.
      </Typography>

      <Stack spacing={2}>
        <SettingSection
          title="GitHub"
          icon={<GitHubIcon />}
          isEditable={canEditConnections}
          isLoading={loadingSection === 'GitHub'}
          apiFeedback={feedback['GitHub']}
          onSave={() => handleSave('GitHub', 'github', githubConfig.name, { url: githubConfig.url, token: githubConfig.token })}
          onRequestChange={() => handleRequestChange('GitHub')}
        >
          <Typography color="text.secondary">Provide a unique name for this connection.</Typography>
          <TextField fullWidth label="Connection Name" placeholder="e.g., Personal GitHub" value={githubConfig.name} onChange={(e) => setGithubConfig(p => ({...p, name: e.target.value}))} />
          <TextField fullWidth label="GitHub Repository URL" placeholder="https://github.com/owner/repo" value={githubConfig.url} onChange={(e) => setGithubConfig(p => ({...p, url: e.target.value}))} />
          <TextField fullWidth type="password" label="GitHub Personal Access Token" value={githubConfig.token} onChange={(e) => setGithubConfig(p => ({...p, token: e.target.value}))} />
        </SettingSection>

        <SettingSection
          title="Database"
          icon={<DnsIcon />}
          isEditable={canEditConnections}
          isLoading={loadingSection === 'Database'}
          apiFeedback={feedback['Database']}
          onSave={() => handleSave('Database', dbType, dbConfig.name, dbConfig)}
          onRequestChange={() => handleRequestChange('Database')}
        >
            <Typography color="text.secondary">Select a database type and provide the connection details.</Typography>
            <TextField fullWidth label="Connection Name" placeholder="e.g., Production Postgres" value={dbConfig.name} onChange={(e) => setDbConfig(p => ({...p, name: e.target.value}))} />
            <FormControl fullWidth>
                <InputLabel>Database Type</InputLabel>
                <Select value={dbType} label="Database Type" onChange={(e: SelectChangeEvent) => setDbType(e.target.value)}>
                    <MenuItem value="postgres">PostgreSQL</MenuItem>
                    <MenuItem value="mysql">MySQL</MenuItem>
                    <MenuItem value="sqlserver">SQL Server</MenuItem>
                    <MenuItem value="mongodb">MongoDB</MenuItem>
                    <MenuItem value="redis">Redis</MenuItem>
                    <MenuItem value="redshift">Amazon Redshift</MenuItem>
                    <MenuItem value="snowflake">Snowflake</MenuItem>
                    <MenuItem value="bigquery">Google BigQuery</MenuItem>
                </Select>
            </FormControl>
            {renderDbFields()}
        </SettingSection>

        <SettingSection
          title="Cloud Storage"
          icon={<StorageIcon />}
          isEditable={canEditConnections}
          isLoading={loadingSection === 'Storage'}
          apiFeedback={feedback['Storage']}
          onSave={() => handleSave('Storage', storageType, storageConfig.name, storageConfig)}
          onRequestChange={() => handleRequestChange('Storage')}
        >
            <Typography color="text.secondary">Connect to a cloud storage provider for file-based resources.</Typography>
            <TextField fullWidth label="Connection Name" placeholder="e.g., Project Assets S3" value={storageConfig.name} onChange={(e) => setStorageConfig(p => ({...p, name: e.target.value}))} />
            <FormControl fullWidth>
                <InputLabel>Storage Provider</InputLabel>
                <Select value={storageType} label="Storage Provider" onChange={(e: SelectChangeEvent) => setStorageType(e.target.value)}>
                    <MenuItem value="s3">AWS S3</MenuItem>
                    <MenuItem value="gcs">Google Cloud Storage</MenuItem>
                    <MenuItem value="azure">Azure Blob Storage</MenuItem>
                </Select>
            </FormControl>
            {renderStorageFields()}
        </SettingSection>
      </Stack>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Other Integrations
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Configure connections to cloud logging services for advanced monitoring.
      </Typography>

      <Stack spacing={2}>
        <SettingSection
          title="Google Cloud Logging"
          icon={<Avatar sx={{ bgcolor: '#4285F4' }}><CloudIcon /></Avatar>}
          isEditable={canEditConnections}
          isLoading={loadingSection === 'GCP'}
          apiFeedback={feedback['GCP']}
          onSave={() => handleSave('GCP', 'gcp-logging', gcpConfig.name, { credentials: gcpConfig.credentials })}
          onRequestChange={() => handleRequestChange('GCP')}
        >
            <Typography color="text.secondary">Provide your GCP Service Account JSON to connect to Cloud Logging.</Typography>
            <TextField fullWidth label="Connection Name" placeholder="e.g., Main GCP Project" value={gcpConfig.name} onChange={(e) => setGcpConfig(p => ({...p, name: e.target.value}))} />
            <TextField fullWidth multiline rows={4} label="GCP Service Account JSON" placeholder='Paste your service account JSON here' value={gcpConfig.credentials} onChange={(e) => setGcpConfig(p => ({...p, credentials: e.target.value}))} />
        </SettingSection>

        <SettingSection
          title="Azure Monitor Logs"
          icon={<Avatar sx={{ bgcolor: '#0078D4' }}><CloudIcon /></Avatar>}
          isEditable={canEditConnections}
          isLoading={loadingSection === 'Azure'}
          apiFeedback={feedback['Azure']}
          onSave={() => handleSave('Azure', 'azure-monitor', azureConfig.name, { credentials: azureConfig.credentials })}
          onRequestChange={() => handleRequestChange('Azure')}
        >
            <Typography color="text.secondary">Provide your Azure Service Principal credentials to connect to Azure Monitor.</Typography>
            <TextField fullWidth label="Connection Name" placeholder="e.g., Production Azure Subscription" value={azureConfig.name} onChange={(e) => setAzureConfig(p => ({...p, name: e.target.value}))} />
            <TextField fullWidth multiline rows={4} label="Azure Service Principal JSON" placeholder='Paste your service principal JSON here' value={azureConfig.credentials} onChange={(e) => setAzureConfig(p => ({...p, credentials: e.target.value}))} />
        </SettingSection>

        <SettingSection
          title="AWS CloudWatch Logs"
          icon={<Avatar sx={{ bgcolor: '#FF9900' }}><CloudIcon /></Avatar>}
          isEditable={canEditConnections}
          isLoading={loadingSection === 'AWS'}
          apiFeedback={feedback['AWS']}
          onSave={() => handleSave('AWS', 'cloudwatch', awsConfig.name, { accessKeyId: awsConfig.accessKeyId, secretAccessKey: awsConfig.secretAccessKey })}
          onRequestChange={() => handleRequestChange('AWS')}
        >
            <Typography color="text.secondary">Provide your AWS Access Keys to connect to CloudWatch.</Typography>
            <TextField fullWidth label="Connection Name" placeholder="e.g., Main AWS Account" value={awsConfig.name} onChange={(e) => setAwsConfig(p => ({...p, name: e.target.value}))} />
            <TextField fullWidth label="AWS Access Key ID" value={awsConfig.accessKeyId} onChange={(e) => setAwsConfig(p => ({...p, accessKeyId: e.target.value}))} />
            <TextField fullWidth type="password" label="AWS Secret Access Key" value={awsConfig.secretAccessKey} onChange={(e) => setAwsConfig(p => ({...p, secretAccessKey: e.target.value}))} />
        </SettingSection>
      </Stack>
    </Box>
  );
};

export default SettingsPage;